import { useCallback, useMemo, useRef } from "react";

import { sendOnePlusXEvent } from "~/components/tracking/one-plus-x";
import type { UseParsedCookieOption } from "~/lib/hooks";
import { useParsedCookie } from "~/lib/hooks";
import { isString } from "~/lib/utils";

import { Edition, editionOptions } from "./data";

export * from "./data";

const isValidEdition = (edition: string): edition is Edition =>
  Object.values(Edition).includes(edition as Edition);

export function useEdition(option?: UseParsedCookieOption) {
  const { update: updateCookie, value } = useParsedCookie("scmp_edition", raw => raw, option);
  const isInitialHasSelected = useRef(value !== null);

  const get = useCallback(
    () => (isString(value) && isValidEdition(value) ? value : Edition.International),
    [value],
  );

  const getOptions = useCallback(() => editionOptions, []);

  const update = useCallback(
    (value: string) => {
      if (!isValidEdition(value)) return;

      sendOnePlusXEvent({
        edition: value,
        type: "edition-preference",
      });
      updateCookie(value);
    },
    [updateCookie],
  );

  return {
    get,
    getOptions,
    isInitialHasSelected: isInitialHasSelected.current,
    update,
  };
}

export function useEditionValue(option?: UseParsedCookieOption): Edition {
  const { get } = useEdition(option);
  const value = useMemo(() => get(), [get]);
  return value;
}
